import { defineNuxtPlugin } from "#app";
import { watch } from "vue";

const PRIMEVUE_LOCALES: Record<string, Record<string, unknown>> = {
  it: {
    firstDayOfWeek: 1,
    dateFormat: "dd/mm/yy",
    dayNames: [
      "Domenica",
      "Lunedì",
      "Martedì",
      "Mercoledì",
      "Giovedì",
      "Venerdì",
      "Sabato",
    ],
    dayNamesShort: ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"],
    dayNamesMin: ["Do", "Lu", "Ma", "Me", "Gi", "Ve", "Sa"],
    monthNames: [
      "Gennaio",
      "Febbraio",
      "Marzo",
      "Aprile",
      "Maggio",
      "Giugno",
      "Luglio",
      "Agosto",
      "Settembre",
      "Ottobre",
      "Novembre",
      "Dicembre",
    ],
    monthNamesShort: [
      "Gen",
      "Feb",
      "Mar",
      "Apr",
      "Mag",
      "Giu",
      "Lug",
      "Ago",
      "Set",
      "Ott",
      "Nov",
      "Dic",
    ],
    today: "Oggi",
    clear: "Cancella",
    weekHeader: "Sett",
    chooseYear: "Scegli anno",
    chooseMonth: "Scegli mese",
    chooseDate: "Scegli data",
    prevDecade: "Decennio precedente",
    nextDecade: "Decennio successivo",
    prevYear: "Anno precedente",
    nextYear: "Anno successivo",
    prevMonth: "Mese precedente",
    nextMonth: "Mese successivo",
  },
  en: {
    firstDayOfWeek: 1,
    dateFormat: "dd/mm/yy",
    dayNames: [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ],
    dayNamesShort: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    dayNamesMin: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
    monthNames: [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
    monthNamesShort: [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ],
    today: "Today",
    clear: "Clear",
    weekHeader: "Wk",
    chooseYear: "Choose Year",
    chooseMonth: "Choose Month",
    chooseDate: "Choose Date",
    prevDecade: "Previous Decade",
    nextDecade: "Next Decade",
    prevYear: "Previous Year",
    nextYear: "Next Year",
    prevMonth: "Previous Month",
    nextMonth: "Next Month",
  },
  fr: {
    firstDayOfWeek: 1,
    dateFormat: "dd/mm/yy",
    dayNames: [
      "Dimanche",
      "Lundi",
      "Mardi",
      "Mercredi",
      "Jeudi",
      "Vendredi",
      "Samedi",
    ],
    dayNamesShort: ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"],
    dayNamesMin: ["Di", "Lu", "Ma", "Me", "Je", "Ve", "Sa"],
    monthNames: [
      "Janvier",
      "Février",
      "Mars",
      "Avril",
      "Mai",
      "Juin",
      "Juillet",
      "Août",
      "Septembre",
      "Octobre",
      "Novembre",
      "Décembre",
    ],
    monthNamesShort: [
      "Jan",
      "Fév",
      "Mar",
      "Avr",
      "Mai",
      "Jun",
      "Jul",
      "Aoû",
      "Sep",
      "Oct",
      "Nov",
      "Déc",
    ],
    today: "Aujourd'hui",
    clear: "Effacer",
    weekHeader: "Sem",
    chooseYear: "Choisir l'année",
    chooseMonth: "Choisir le mois",
    chooseDate: "Choisir la date",
    prevDecade: "Décennie précédente",
    nextDecade: "Décennie suivante",
    prevYear: "Année précédente",
    nextYear: "Année suivante",
    prevMonth: "Mois précédent",
    nextMonth: "Mois suivant",
  },
  es: {
    firstDayOfWeek: 1,
    dateFormat: "dd/mm/yy",
    dayNames: [
      "Domingo",
      "Lunes",
      "Martes",
      "Miércoles",
      "Jueves",
      "Viernes",
      "Sábado",
    ],
    dayNamesShort: ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"],
    dayNamesMin: ["Do", "Lu", "Ma", "Mi", "Ju", "Vi", "Sá"],
    monthNames: [
      "Enero",
      "Febrero",
      "Marzo",
      "Abril",
      "Mayo",
      "Junio",
      "Julio",
      "Agosto",
      "Septiembre",
      "Octubre",
      "Noviembre",
      "Diciembre",
    ],
    monthNamesShort: [
      "Ene",
      "Feb",
      "Mar",
      "Abr",
      "May",
      "Jun",
      "Jul",
      "Ago",
      "Sep",
      "Oct",
      "Nov",
      "Dic",
    ],
    today: "Hoy",
    clear: "Borrar",
    weekHeader: "Sem",
    chooseYear: "Elegir año",
    chooseMonth: "Elegir mes",
    chooseDate: "Elegir fecha",
    prevDecade: "Década anterior",
    nextDecade: "Siguiente década",
    prevYear: "Año anterior",
    nextYear: "Año siguiente",
    prevMonth: "Mes anterior",
    nextMonth: "Mes siguiente",
  },
};

export default defineNuxtPlugin((nuxtApp) => {
  const applyLocale = (lang: string) => {
    const localeData = PRIMEVUE_LOCALES[lang] ?? PRIMEVUE_LOCALES.it;
    const $primevue = nuxtApp.vueApp.config.globalProperties.$primevue;
    if ($primevue?.config?.locale) {
      Object.assign($primevue.config.locale, localeData);
    }
  };

  nuxtApp.hook("app:mounted", () => {
    // $i18n is available on nuxtApp after the i18n module is set up
    const i18n = nuxtApp.$i18n as
      | { locale: import("vue").Ref<string> }
      | undefined;
    if (!i18n) return;

    applyLocale(i18n.locale.value);
    watch(i18n.locale, (newLocale) => applyLocale(newLocale));
  });
});
